import { apiFetch } from "./api.js";

const STORAGE_PREFIX = "nitro_shop_checkout_intent_v1";
const LOCK_PREFIX = "nitro:shop-checkout-intent:v1";
const VALID_KEY = /^[A-Za-z0-9._:-]{8,190}$/;
const VALID_STATUS = new Set(["planned", "processing", "uncertain", "requires_action", "completed", "failed"]);
const RECONCILABLE_STATUS = new Set(["planned", "processing", "uncertain", "requires_action"]);
const PAYPAL_HOSTS = new Set(["www.paypal.com", "www.sandbox.paypal.com"]);

export async function createCheckoutIntent(payload, actorId, options = {}) {
  const cryptoApi = cryptoFor(options);
  const key = cryptoApi.randomUUID();
  if (!VALID_KEY.test(key)) throw checkoutIntentError("CHECKOUT_INTENT_KEY_INVALID", "No fue posible preparar una identidad segura para la compra.");
  const now = options.now?.() || new Date();
  return {
    v: 1,
    key,
    status: "prepared",
    commercialFingerprint: await checkoutCommercialFingerprint(payload, key, { cryptoApi }),
    actorBinding: await checkoutActorBinding(actorId, { cryptoApi }),
    provider: null,
    orderNumber: null,
    createdAt: now.toISOString(),
    updatedAt: now.toISOString(),
  };
}

export async function checkoutCommercialFingerprint(payload, key, options = {}) {
  if (!VALID_KEY.test(String(key || ""))) throw checkoutIntentError("CHECKOUT_INTENT_KEY_INVALID", "La identidad de la compra no es valida.");
  return sha256(`nitro-checkout-commercial:v1:${key}:${canonicalJson(payload || {})}`, cryptoFor(options));
}

export async function checkoutActorBinding(actorId, options = {}) {
  const normalized = String(actorId ?? "").trim();
  if (!normalized) throw checkoutIntentError("CHECKOUT_ACTOR_REQUIRED", "Debes iniciar sesion para continuar con la compra.");
  return sha256(`nitro-checkout-actor:v1:${normalized}`, cryptoFor(options));
}

export async function loadCheckoutIntent(actorId, options = {}) {
  const storage = storageFor(options);
  const storageKey = await checkoutStorageKey(actorId, options);
  const expectedActor = await checkoutActorBinding(actorId, options);
  // Read after every async identity check so an old snapshot cannot cross them.
  const current = readStoredIntent(storage, storageKey, expectedActor);
  if (current) return Object.freeze({ ...current });
  const legacy = readOptionalStoredIntent(legacyStorageFor(options), storageKey, expectedActor);
  return legacy ? Object.freeze({ ...legacy }) : null;
}

export async function saveCheckoutIntent(actorId, intent, options = {}) {
  if (!isStoredIntent(intent)) throw checkoutIntentError("CHECKOUT_INTENT_STORAGE_INVALID", "El estado de la compra no es valido.");
  const expectedActor = await checkoutActorBinding(actorId, options);
  if (intent.actorBinding !== expectedActor) throw checkoutIntentError("CHECKOUT_INTENT_ACTOR_MISMATCH", "La compra pendiente pertenece a otra sesion.");
  const storage = storageFor(options);
  const storageKey = await checkoutStorageKey(actorId, options);
  const safeIntent = storedIntent(intent);
  return withCheckoutLock(expectedActor, options, null, () => {
    const current = readStoredIntent(storage, storageKey, expectedActor);
    if (current ? current.key !== intent.key : false) return null;
    const legacy = current ? null : readOptionalStoredIntent(legacyStorageFor(options), storageKey, expectedActor);
    if (legacy ? legacy.key !== intent.key : !current && options.createIfAbsent !== true) return null;
    writeStoredIntent(storage, storageKey, safeIntent);
    return Object.freeze(safeIntent);
  });
}

export async function clearCheckoutIntent(actorId, key, options = {}) {
  const storage = storageFor(options);
  const expectedActor = await checkoutActorBinding(actorId, options);
  const storageKey = await checkoutStorageKey(actorId, options);
  return withCheckoutLock(expectedActor, options, false, () => {
    const current = readStoredIntent(storage, storageKey, expectedActor);
    const legacyStorage = legacyStorageFor(options);
    const legacy = readOptionalStoredIntent(legacyStorage, storageKey, expectedActor);
    if (current && current.key !== key) return false;
    if (legacy && legacy.key !== key) return false;
    if (!current && !legacy) return false;
    if (current) removeStoredIntent(storage, storageKey);
    if (legacy) removeStoredIntent(legacyStorage, storageKey);
    return true;
  });
}

export async function claimCheckoutIntent(actorId, payload, options = {}) {
  const expectedActor = await checkoutActorBinding(actorId, options);
  const storage = storageFor(options);
  const storageKey = `${STORAGE_PREFIX}:${expectedActor}`;
  return withCheckoutLock(expectedActor, options, null, async () => {
    const existing = readStoredIntent(storage, storageKey, expectedActor);
    if (existing) return { intent: Object.freeze({ ...existing }), created: false };
    const legacy = readOptionalStoredIntent(legacyStorageFor(options), storageKey, expectedActor);
    if (legacy) {
      writeStoredIntent(storage, storageKey, legacy);
      return { intent: Object.freeze({ ...legacy }), created: false };
    }
    const candidate = await createCheckoutIntent(payload, actorId, options);
    if (typeof options.guard === "function" && !options.guard()) {
      throw checkoutIntentError("CHECKOUT_INTENT_CHANGED", "La compra cambió durante la preparación. Consulta su estado antes de continuar.");
    }
    const current = readStoredIntent(storage, storageKey, expectedActor);
    if (current) return { intent: Object.freeze({ ...current }), created: false };
    const safeIntent = storedIntent({ ...candidate, status: "submitting" });
    writeStoredIntent(storage, storageKey, safeIntent);
    return { intent: Object.freeze(safeIntent), created: true };
  });
}

export async function hasCheckoutDraftChanged(intent, payload, options = {}) {
  if (!intent) return false;
  return intent.commercialFingerprint !== await checkoutCommercialFingerprint(payload, intent.key, options);
}

export async function postCheckoutWithReconciliation({ key, payload, request = apiFetch } = {}) {
  if (!VALID_KEY.test(String(key || ""))) throw checkoutIntentError("CHECKOUT_INTENT_KEY_INVALID", "La identidad de la compra no es valida.");
  try {
    const data = await request("/api/v1/shop/checkout", {
      method: "POST",
      headers: { "Idempotency-Key": key },
      body: JSON.stringify(payload || {}),
      timeoutMs: 15000,
    });
    return { source: "post", found: true, result: normalizeCheckoutIntentResult(data, key) };
  } catch (error) {
    if (!shouldReconcileCheckoutError(error)) throw error;
    const inspection = await inspectCheckoutIntent(key, { request });
    return { source: "inspection", ...inspection };
  }
}

export async function inspectCheckoutIntent(key, { request = apiFetch } = {}) {
  if (!VALID_KEY.test(String(key || ""))) throw checkoutIntentError("CHECKOUT_INTENT_KEY_INVALID", "La identidad de la compra no es valida.");
  let data;
  try {
    data = await request(`/api/v1/shop/checkout-intents/${encodeURIComponent(key)}`, {
      cache: "no-store",
      timeoutMs: 12000,
    });
  } catch (error) {
    if (error?.status === 404) return { found: false, key, status: "unknown", absenceConfirmed: false };
    throw error;
  }
  if (data?.found === false) {
    if (data.idempotencyKey !== key) throw checkoutIntentError("CHECKOUT_RESPONSE_INVALID", "No pudimos verificar la identidad de la compra consultada.");
    return { found: false, key, status: "unknown", absenceConfirmed: true };
  }
  return { found: true, result: normalizeCheckoutIntentResult(data, key, { requireFound: true }) };
}

// Called only by the explicit retry control. Never replaces or clears the key.
export async function retryCheckoutWithSameIntent({ actorId, intent, getCurrentPayload, guard = () => true, request = apiFetch, options = {} } = {}) {
  if (intent?.status !== "unknown" || typeof getCurrentPayload !== "function") {
    throw checkoutIntentError("CHECKOUT_RETRY_NOT_ALLOWED", "Consulta primero el estado de esta compra.");
  }
  const actorBinding = await checkoutActorBinding(actorId, options);
  return withCheckoutLock(actorBinding, options, null, async () => {
    const current = await loadCheckoutIntent(actorId, options);
    if (!current || current.status !== "unknown" || current.key !== intent.key || current.commercialFingerprint !== intent.commercialFingerprint) {
      throw checkoutIntentError("CHECKOUT_INTENT_CHANGED", "La compra pendiente cambió. Consulta su estado antes de continuar.");
    }
    const assertCurrent = async () => {
      const payload = getCurrentPayload();
      if (await hasCheckoutDraftChanged(intent, payload) || !guard()) {
        throw checkoutIntentError("CHECKOUT_INTENT_CHANGED", "El carrito o los datos cambiaron. Restaura los datos de esta compra antes de reintentar.");
      }
      return payload;
    };
    await assertCurrent();
    const inspection = await inspectCheckoutIntent(intent.key, { request });
    const payload = await assertCurrent();
    if (inspection.found || !inspection.absenceConfirmed) return { source: "inspection", ...inspection };
    return postCheckoutWithReconciliation({ key: intent.key, payload, request });
  });
}

export function normalizeCheckoutIntentResult(data, key, { requireFound = false } = {}) {
  const valid = data && typeof data === "object"
    && (!requireFound || data.found === true)
    && data.idempotencyKey === key
    && VALID_STATUS.has(data.status)
    && typeof data.orderNumber === "string"
    && data.orderNumber.trim().length > 0 && data.orderNumber.length <= 40
    && !/[\u0000-\u001f\u007f]/.test(data.orderNumber)
    && typeof data.provider === "string"
    && ["wompi", "paypal"].includes(data.provider.trim().toLowerCase())
    && (data.paymentUrl == null || typeof data.paymentUrl === "string");
  if (!valid) throw checkoutIntentError("CHECKOUT_RESPONSE_INVALID", "Nitro devolvio un estado de compra que no se pudo verificar.");
  const result = {
    idempotencyKey: key,
    status: data.status,
    orderNumber: clean(data.orderNumber) || null,
    provider: clean(data.provider).toLowerCase() || null,
    paymentUrl: clean(data.paymentUrl) || null,
    errorCode: safeCode(data.errorCode),
    createdAt: safeDate(data.createdAt),
    completedAt: safeDate(data.completedAt),
  };
  if (result.status === "requires_action" && result.provider === "paypal" && !checkoutNavigation(result)) {
    throw checkoutIntentError("CHECKOUT_RESPONSE_INVALID", "El destino de pago no pudo verificarse.");
  }
  return Object.freeze(result);
}

export function checkoutNavigation(result) {
  if (!result || result.status !== "requires_action" || result.errorCode === "PAYMENT_URL_INVALID") return null;
  if (result.provider === "wompi") {
    const orderNumber = clean(result.orderNumber);
    return orderNumber ? `/pagos/wompi?reference=${encodeURIComponent(orderNumber)}` : null;
  }
  if (result.provider !== "paypal" || !result.paymentUrl) return null;
  try {
    const target = new URL(result.paymentUrl);
    if (target.protocol !== "https:" || target.username || target.password || target.port || target.hash || !PAYPAL_HOSTS.has(target.hostname.toLowerCase())) return null;
    return target.href;
  } catch {
    return null;
  }
}

export function automaticCheckoutNavigation(outcome) {
  if (outcome?.source !== "post" || !outcome?.found) return null;
  return checkoutNavigation(outcome.result);
}

export function isCheckoutIntentBlocking(intent) {
  return Boolean(intent && intent.status !== "completed");
}

export function isCheckoutIntentReconcilable(intent) {
  return Boolean(intent && (intent.status === "unknown" || RECONCILABLE_STATUS.has(intent.status)));
}

export function updateStoredCheckoutIntent(intent, outcome, options = {}) {
  const now = options.now?.() || new Date();
  if (!outcome?.found) {
    return storedIntent({ ...intent, status: "unknown", updatedAt: now.toISOString() });
  }
  const result = outcome.result;
  return storedIntent({
    ...intent,
    status: result.status,
    provider: result.provider,
    orderNumber: result.orderNumber,
    updatedAt: now.toISOString(),
  });
}

export function shouldReconcileCheckoutError(error) {
  if (error?.code === "CHECKOUT_RESPONSE_INVALID" || error?.code === "NITRO_INVALID_RESPONSE") return true;
  if (Number(error?.status) >= 500) return true;
  return error?.status === undefined || error?.status === null;
}

export function runCheckoutSingleFlight(holder, factory) {
  if (!holder || typeof holder !== "object" || typeof factory !== "function") throw new TypeError("holder and factory are required");
  if (holder.current) return holder.current;
  const operation = Promise.resolve().then(factory);
  holder.current = operation;
  const release = () => {
    if (holder.current === operation) holder.current = null;
  };
  operation.then(release, release);
  return operation;
}

function isStoredIntent(value) {
  return Boolean(value && value.v === 1 && VALID_KEY.test(value.key)
    && typeof value.commercialFingerprint === "string" && /^[a-f0-9]{64}$/.test(value.commercialFingerprint)
    && typeof value.actorBinding === "string" && /^[a-f0-9]{64}$/.test(value.actorBinding)
    && ["prepared", "submitting", "unknown", ...VALID_STATUS].includes(value.status)
    && (!value.provider || typeof value.provider === "string")
    && (!value.orderNumber || typeof value.orderNumber === "string"));
}

function storedIntent(intent) {
  return {
    v: 1,
    key: intent.key,
    status: intent.status,
    commercialFingerprint: intent.commercialFingerprint,
    actorBinding: intent.actorBinding,
    provider: clean(intent.provider).toLowerCase() || null,
    orderNumber: clean(intent.orderNumber) || null,
    createdAt: safeDate(intent.createdAt) || new Date().toISOString(),
    updatedAt: safeDate(intent.updatedAt) || new Date().toISOString(),
  };
}

async function checkoutStorageKey(actorId, options) {
  return `${STORAGE_PREFIX}:${await checkoutActorBinding(actorId, options)}`;
}

function readStoredIntent(storage, storageKey, expectedActor) {
  let current;
  try { current = JSON.parse(storage.getItem(storageKey) || "null"); }
  catch { throw checkoutIntentError("CHECKOUT_INTENT_STORAGE_INVALID", "El estado local de la compra no se pudo verificar."); }
  if (!current) return null;
  if (!isStoredIntent(current)) throw checkoutIntentError("CHECKOUT_INTENT_STORAGE_INVALID", "El estado local de la compra no se pudo verificar.");
  if (current.actorBinding !== expectedActor) throw checkoutIntentError("CHECKOUT_INTENT_ACTOR_MISMATCH", "La compra pendiente pertenece a otra sesion.");
  return current;
}

function storageFor(options) {
  let storage;
  try {
    storage = Object.prototype.hasOwnProperty.call(options, "storage") ? options.storage : globalThis.localStorage;
  } catch {
    throw checkoutIntentError("CHECKOUT_INTENT_STORAGE_UNAVAILABLE", "El navegador no puede conservar la identidad de la compra.");
  }
  if (!storage || typeof storage.getItem !== "function" || typeof storage.setItem !== "function" || typeof storage.removeItem !== "function") {
    throw checkoutIntentError("CHECKOUT_INTENT_STORAGE_UNAVAILABLE", "El navegador no puede conservar la identidad de la compra.");
  }
  return storage;
}

function legacyStorageFor(options) {
  let storage;
  try {
    if (Object.prototype.hasOwnProperty.call(options, "legacyStorage")) storage = options.legacyStorage;
    else if (Object.prototype.hasOwnProperty.call(options, "storage")) storage = null;
    else storage = globalThis.sessionStorage;
  } catch {
    throw checkoutIntentError("CHECKOUT_INTENT_STORAGE_UNAVAILABLE", "El navegador no pudo comprobar una compra pendiente de una sesión anterior.");
  }
  if (storage == null) return null;
  if (typeof storage.getItem !== "function" || typeof storage.removeItem !== "function") {
    throw checkoutIntentError("CHECKOUT_INTENT_STORAGE_UNAVAILABLE", "El navegador no pudo comprobar una compra pendiente de una sesión anterior.");
  }
  return storage;
}

function readOptionalStoredIntent(storage, storageKey, expectedActor) {
  return storage ? readStoredIntent(storage, storageKey, expectedActor) : null;
}

async function withCheckoutLock(actorBinding, options, staleValue, operation) {
  if (typeof options.guard === "function" && !options.guard()) return staleValue;
  const lockManager = lockManagerFor(options);
  const lockName = `${LOCK_PREFIX}:${actorBinding}`;
  try {
    return await lockManager.request(lockName, { mode: "exclusive", ifAvailable: true }, async (lock) => {
      if (!lock) throw checkoutIntentError("CHECKOUT_INTENT_LOCK_BUSY", "Otra pestaña está actualizando esta compra. Consulta su estado antes de continuar.");
      if (typeof options.guard === "function" && !options.guard()) return staleValue;
      return operation();
    });
  } catch (error) {
    if (error?.code) throw error;
    throw checkoutIntentError("CHECKOUT_INTENT_LOCK_UNAVAILABLE", "El navegador no pudo bloquear de forma segura la identidad de la compra.");
  }
}

function lockManagerFor(options) {
  let lockManager;
  try {
    lockManager = Object.prototype.hasOwnProperty.call(options, "lockManager") ? options.lockManager : globalThis.navigator?.locks;
  } catch {
    throw checkoutIntentError("CHECKOUT_INTENT_LOCK_UNAVAILABLE", "El navegador no pudo bloquear de forma segura la identidad de la compra.");
  }
  if (!lockManager || typeof lockManager.request !== "function") {
    throw checkoutIntentError("CHECKOUT_INTENT_LOCK_UNAVAILABLE", "El navegador no pudo bloquear de forma segura la identidad de la compra.");
  }
  return lockManager;
}

function writeStoredIntent(storage, storageKey, intent) {
  try {
    storage.setItem(storageKey, JSON.stringify(intent));
  } catch {
    throw checkoutIntentError("CHECKOUT_INTENT_STORAGE_UNAVAILABLE", "El navegador no pudo conservar la identidad de la compra.");
  }
}

function removeStoredIntent(storage, storageKey) {
  try {
    storage.removeItem(storageKey);
  } catch {
    throw checkoutIntentError("CHECKOUT_INTENT_STORAGE_UNAVAILABLE", "El navegador no pudo cerrar la compra anterior.");
  }
}

function cryptoFor(options) {
  const cryptoApi = options.cryptoApi || globalThis.crypto;
  if (!cryptoApi?.subtle || typeof cryptoApi.randomUUID !== "function") {
    throw checkoutIntentError("CHECKOUT_INTENT_CRYPTO_UNAVAILABLE", "El navegador no puede preparar una identidad segura para la compra.");
  }
  return cryptoApi;
}

async function sha256(value, cryptoApi) {
  const bytes = new TextEncoder().encode(value);
  const digest = await cryptoApi.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function canonicalJson(value) {
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.keys(value).sort().map((key) => `${JSON.stringify(key)}:${canonicalJson(value[key])}`).join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}

function checkoutIntentError(code, message) {
  const error = new Error(message);
  error.code = code;
  return error;
}

function safeDate(value) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function safeCode(value) {
  const code = clean(value).toUpperCase();
  return /^[A-Z0-9_]{1,80}$/.test(code) ? code : null;
}

function clean(value) {
  return String(value ?? "").trim();
}
